import React, { useEffect, useMemo, useState } from 'react';
import { usePromptManagement } from '../../hooks/usePromptManagement';
import './PromptManagementPage.css';

const blankForm = {
  module_id: '',
  prompt_type: '',
  submodule_order: '',
  prompt_text: '',
};

const PromptManagementPage = ({ callerUserId }) => {
  const pm = usePromptManagement(callerUserId);
  const [tab, setTab] = useState('list');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    pm.loadModules();
    pm.loadPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callerUserId]);

  const modulesById = useMemo(() => {
    const m = {};
    pm.modules.forEach((x) => { m[x.module_id] = x.module_name; });
    return m;
  }, [pm.modules]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return pm.prompts;
    return pm.prompts.filter((p) => {
      const hay = `${p.module_name || ''} ${p.prompt_type || ''} ${p.prompt_text || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [pm.prompts, filter]);

  const startCreate = () => {
    setEditingId(null);
    setForm(blankForm);
    setTab('edit');
  };

  const startEdit = (p) => {
    setEditingId(p.prompt_id);
    setForm({
      module_id: String(p.module_id),
      prompt_type: p.prompt_type || '',
      submodule_order: String(p.submodule_order ?? ''),
      prompt_text: p.prompt_text || '',
    });
    setTab('edit');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(blankForm);
    setTab('list');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.prompt_text.trim()) { alert('Prompt text is required.'); return; }
    setSaving(true);
    let res;
    if (editingId) {
      res = await pm.updatePrompt(editingId, { prompt_text: form.prompt_text });
    } else {
      if (!form.module_id) { setSaving(false); alert('Select a module.'); return; }
      if (form.submodule_order === '' || isNaN(parseInt(form.submodule_order, 10))) {
        setSaving(false);
        alert('Submodule order must be an integer.');
        return;
      }
      res = await pm.createPrompt({
        module_id: parseInt(form.module_id, 10),
        prompt_type: form.prompt_type.trim() || null,
        submodule_order: parseInt(form.submodule_order, 10),
        prompt_text: form.prompt_text,
      });
    }
    setSaving(false);
    if (res.success) {
      await pm.loadPrompts();
      cancelEdit();
    } else {
      alert(res.error);
    }
  };

  return (
    <div className="sysadmin-container">
      <h2>Prompt Management</h2>

      <div className="sysadmin-tabs">
        <button
          className={tab === 'list' ? 'active' : ''}
          onClick={() => setTab('list')}
        >
          All Prompts
        </button>
        <button
          className={tab === 'edit' ? 'active' : ''}
          onClick={startCreate}
        >
          {editingId ? 'Edit Prompt' : 'New Prompt'}
        </button>
      </div>

      {pm.error && <div className="sysadmin-error">{pm.error}</div>}

      {tab === 'list' && (
        <div className="sysadmin-panel">
          <div className="sysadmin-search">
            <input
              type="text"
              placeholder="Filter by module, type, or text..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <button onClick={startCreate}>+ New prompt</button>
          </div>

          <table className="sysadmin-table">
            <thead>
              <tr>
                <th>Prompt ID</th>
                <th>Module</th>
                <th>Type</th>
                <th>Order</th>
                <th>Prompt text</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.prompt_id}>
                  <td>{p.prompt_id}</td>
                  <td>{p.module_name}</td>
                  <td>{p.prompt_type || ''}</td>
                  <td>{p.submodule_order}</td>
                  <td className="prompt-text-cell">
                    {(p.prompt_text || '').slice(0, 160)}
                    {p.prompt_text && p.prompt_text.length > 160 ? '…' : ''}
                  </td>
                  <td>
                    <button onClick={() => startEdit(p)}>Edit</button>
                  </td>
                </tr>
              ))}
              {!filtered.length && !pm.loading && (
                <tr><td colSpan="6">No prompts found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'edit' && (
        <div className="sysadmin-panel">
          <form className="sysadmin-form prompt-form" onSubmit={handleSave}>
            {editingId && (
              <label>
                Prompt ID
                <input type="text" value={editingId} readOnly />
              </label>
            )}
            <label>
              Module
              {editingId ? (
                <input
                  type="text"
                  value={modulesById[form.module_id] || form.module_id}
                  readOnly
                />
              ) : (
                <select
                  value={form.module_id}
                  onChange={(e) => setForm({ ...form, module_id: e.target.value })}
                  required
                >
                  <option value="">-- select a module --</option>
                  {pm.modules.map((m) => (
                    <option key={m.module_id} value={m.module_id}>
                      {m.module_name}
                    </option>
                  ))}
                </select>
              )}
            </label>
            <label>
              Prompt type
              <input
                type="text"
                value={form.prompt_type}
                onChange={(e) => setForm({ ...form, prompt_type: e.target.value })}
                placeholder="e.g. chat_submodule"
                readOnly={!!editingId}
              />
            </label>
            <label>
              Submodule order
              <input
                type="number"
                value={form.submodule_order}
                onChange={(e) => setForm({ ...form, submodule_order: e.target.value })}
                required
                readOnly={!!editingId}
              />
            </label>
            <label>
              Prompt text
              <textarea
                className="prompt-textarea"
                value={form.prompt_text}
                onChange={(e) => setForm({ ...form, prompt_text: e.target.value })}
                required
                rows={20}
              />
            </label>
            <div className="prompt-form-buttons">
              <button type="submit" disabled={saving}>
                {saving ? 'Saving…' : (editingId ? 'Update prompt' : 'Create prompt')}
              </button>
              <button type="button" onClick={cancelEdit}>Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default PromptManagementPage;
